// Pure resolution logic — ported from admin-portal-v5 so both surfaces resolve the live
// version identically. The DB returns every readable version; the client derives which one
// is live and whether it's in scope for the current rooftop.
//
// Correctness depends on the dealer roles' Hasura permission exposing ARCHIVED rows as well as
// published ones (public_learning_modules.yaml): the "archived head = key is dark" rule below
// can only fire if the archived head actually reaches this client. Under a published-only
// filter, archiving the live version would make the previous published version resurface.

import { ModuleSummary, Audience, Tier, ModuleFeature, PRODUCT_FEATURES } from '../types';

export const isProductFeature = (f: ModuleFeature): boolean => PRODUCT_FEATURES.includes(f);

/** The begins_at of the next non-draft version of the same key (which supersedes m), or null. */
export function nextVersionBeginsAt<T extends ModuleSummary>(all: T[], m: T): string | null {
  const mBegins = new Date(m.begins_at).getTime();
  let min: string | null = null;
  for (const other of all) {
    if (other.id === m.id || other.key !== m.key || other.status === 'draft') continue;
    if (new Date(other.begins_at).getTime() <= mBegins) continue;
    if (min == null || new Date(other.begins_at).getTime() < new Date(min).getTime()) min = other.begins_at;
  }
  return min;
}

/** Effective end = earliest of an explicit ends_at and the next version's begins_at. */
export function resolveEffectiveEnd<T extends ModuleSummary>(all: T[], m: T): string | null {
  const explicit = m.ends_at ?? null;
  const auto = nextVersionBeginsAt(all, m);
  if (explicit && auto) return new Date(explicit).getTime() <= new Date(auto).getTime() ? explicit : auto;
  return explicit ?? auto;
}

/**
 * Resolve the live version of every key: the most-recent non-draft row whose window
 * [begins_at, effectiveEnd) contains now, respecting archived/expired.
 */
export function resolveLiveModules<T extends ModuleSummary>(all: T[], now: Date = new Date()): T[] {
  const nowMs = now.getTime();
  const byKey = new Map<string, T[]>();
  for (const m of all) {
    if (m.status === 'draft') continue;
    if (new Date(m.begins_at).getTime() > nowMs) continue; // not started
    const arr = byKey.get(m.key) ?? [];
    arr.push(m);
    byKey.set(m.key, arr);
  }
  const live: T[] = [];
  for (const arr of byKey.values()) {
    arr.sort((a, b) => new Date(b.begins_at).getTime() - new Date(a.begins_at).getTime());
    const head = arr[0];
    if (!head || head.status !== 'published') continue; // archived head = key is dark
    const end = resolveEffectiveEnd(all, head);
    if (end && nowMs >= new Date(end).getTime()) continue; // expired
    live.push(head);
  }
  return live;
}

export interface DealerScope {
  /** The rooftop's tier, or null when it can't be resolved (missing/unrecognized value). */
  tier: Tier | null;
  /** Product features enabled for the rooftop (from effective_config). */
  enabledFeatures: ModuleFeature[];
  /** Audiences the viewer may see — ['dealer'] plus 'driver' if the user is also a driver. */
  audiences: Audience[];
}

/**
 * Visibility for a rooftop: audience overlap, tier match, and — for purchased-product
 * features — the product being enabled.
 *
 * Tier rule: a module with `tiers == null` targets all tiers and always passes. A tier-restricted
 * module passes only when the rooftop's tier is known and listed. When the rooftop's tier can't be
 * resolved (`scope.tier == null`), tier-restricted modules are withheld but tier-agnostic ones
 * still show — so an unrecognized tier narrows the catalog instead of blanking it entirely.
 */
export function filterForRooftop<T extends ModuleSummary>(modules: T[], scope: DealerScope): T[] {
  return modules.filter(m => {
    const audienceOk = m.audiences.some(a => scope.audiences.includes(a));
    const tierOk = m.tiers == null || (scope.tier != null && m.tiers.includes(scope.tier));
    const featureOk = !isProductFeature(m.feature) || scope.enabledFeatures.includes(m.feature);
    return audienceOk && tierOk && featureOk;
  });
}
